// Asset manager — optimized from day one for many FBX/material/texture assets.
// - Manifest-driven preloading with priorities
// - Async progressive loading (critical first, rest in background)
// - Geometry/material caching and reuse (instancing-friendly)
import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { bus } from './bus.js';

const fbxLoader = new FBXLoader();
const texLoader = new THREE.TextureLoader();

const cache = {
  models: new Map(),     // url -> THREE.Group (master copy, cloned on demand)
  textures: new Map(),   // url -> THREE.Texture
  materials: new Map(),  // key -> THREE.Material
};

// Manifest: filled as the user supplies FBX assets. priority: 0 = critical (blocks play), 1 = background.
export const manifest = [
  // { id: 'lion', type: 'fbx', url: 'assets/animals/lion.fbx', priority: 0 },
];

export const assets = {
  async loadTexture(url) {
    if (cache.textures.has(url)) return cache.textures.get(url);
    const tex = await texLoader.loadAsync(url);
    tex.colorSpace = THREE.SRGBColorSpace;
    cache.textures.set(url, tex);
    return tex;
  },

  async loadFBX(url) {
    if (cache.models.has(url)) return cache.models.get(url);
    const model = await fbxLoader.loadAsync(url);
    model.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
    cache.models.set(url, model);
    return model;
  },

  // Get an instance (clone shares geometry + materials — cheap).
  async instance(url) {
    const master = await this.loadFBX(url);
    return master.clone();
  },

  material(key, factory) {
    if (!cache.materials.has(key)) cache.materials.set(key, factory());
    return cache.materials.get(key);
  },

  async preload(onProgress) {
    const critical = manifest.filter((a) => a.priority === 0);
    const background = manifest.filter((a) => a.priority !== 0);
    let done = 0;
    const total = critical.length;
    for (const a of critical) {
      if (a.type === 'fbx') await this.loadFBX(a.url);
      else if (a.type === 'texture') await this.loadTexture(a.url);
      done++;
      onProgress?.(done / Math.max(total, 1));
    }
    // Background assets load without blocking.
    background.forEach((a) => {
      const p = a.type === 'fbx' ? this.loadFBX(a.url) : this.loadTexture(a.url);
      p.then(() => bus.emit('assets:loaded', a.id)).catch((e) => console.warn('[assets] background load failed', a.id, e));
    });
  },
};
